import { useRef, useEffect, useState } from 'react';

type PollStartOpts = {
	initialDelayMs?: number;
};

type PendingSchedule = {
	delayMs: number;
	generation: number;
};

export function usePoll<CallbackT extends () => Promise<void>>(
	_callback: CallbackT,
	opts: {
		intervalMs: number;
		timeoutMs?: number;
	},
) {
	const callback = useRef<CallbackT | undefined>(undefined);
	const lastPoll = useRef<number | undefined>(undefined);
	const startedAt = useRef<number | undefined>(undefined);
	const pausedAt = useRef<number | undefined>(undefined);
	const accumulatedPausedMs = useRef(0);
	const isPolling = useRef(false);
	const isPaused = useRef(false);
	const timerId = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	const generation = useRef(0);
	const isCallbackRunning = useRef(false);
	const pendingSchedule = useRef<PendingSchedule | undefined>(undefined);
	const [isTimeout, setIsTimeout] = useState(false);
	const timeoutMs = useRef<number | undefined>(undefined);
	const intervalMs = useRef(opts.intervalMs);

	useEffect(() => {
		callback.current = _callback;
	}, [_callback]);

	useEffect(() => {
		timeoutMs.current = opts.timeoutMs;
		intervalMs.current = opts.intervalMs;
	}, [opts.intervalMs, opts.timeoutMs]);

	useEffect(() => {
		return () => {
			stop();
		};
	}, []);

	function clearTimer() {
		if (timerId.current !== undefined) {
			clearTimeout(timerId.current);
			timerId.current = undefined;
		}
	}

	function getElapsedActiveMs(now: number) {
		if (startedAt.current === undefined) {
			return 0;
		}

		return now - startedAt.current - accumulatedPausedMs.current;
	}

	function schedule(delayMs: number, currentGeneration = generation.current) {
		clearTimer();

		if (!isPolling.current || isPaused.current || generation.current !== currentGeneration) {
			return;
		}

		if (isCallbackRunning.current) {
			pendingSchedule.current = { delayMs, generation: currentGeneration };
			return;
		}

		if (
			timeoutMs.current &&
			startedAt.current !== undefined &&
			getElapsedActiveMs(Date.now()) + delayMs > timeoutMs.current
		) {
			setIsTimeout(true);
			stop();
			return;
		}

		timerId.current = setTimeout(async () => {
			timerId.current = undefined;

			if (!isPolling.current || isPaused.current || generation.current !== currentGeneration) {
				return;
			}

			lastPoll.current = Date.now();
			isCallbackRunning.current = true;

			try {
				await callback.current?.();
			} finally {
				isCallbackRunning.current = false;
			}

			const pending = pendingSchedule.current;
			pendingSchedule.current = undefined;

			if (pending) {
				schedule(pending.delayMs, pending.generation);
				return;
			}

			if (!isPolling.current || isPaused.current || generation.current !== currentGeneration) {
				return;
			}

			const nextDelay = lastPoll.current
				? Math.max(0, lastPoll.current + intervalMs.current - Date.now())
				: intervalMs.current;

			schedule(nextDelay, currentGeneration);
		}, delayMs);
	}

	function start(startOpts?: PollStartOpts) {
		setIsTimeout(false);

		if (isPolling.current && isPaused.current) {
			resume(startOpts);
			return;
		}

		if (isPolling.current) {
			return;
		}

		startedAt.current = Date.now();
		pausedAt.current = undefined;
		accumulatedPausedMs.current = 0;
		lastPoll.current = undefined;
		isPolling.current = true;
		isPaused.current = false;
		const currentGeneration = ++generation.current;

		schedule(startOpts?.initialDelayMs ?? 0, currentGeneration);
	}

	function pause() {
		if (!isPolling.current || isPaused.current) {
			return;
		}

		isPaused.current = true;
		pausedAt.current = Date.now();
		generation.current++;
		pendingSchedule.current = undefined;
		clearTimer();
	}

	function resume(startOpts?: PollStartOpts) {
		if (!isPolling.current || !isPaused.current) {
			return;
		}

		if (pausedAt.current !== undefined) {
			accumulatedPausedMs.current += Date.now() - pausedAt.current;
			pausedAt.current = undefined;
		}

		isPaused.current = false;
		const currentGeneration = ++generation.current;
		schedule(startOpts?.initialDelayMs ?? 0, currentGeneration);
	}

	function stop() {
		clearTimer();
		generation.current++;
		pendingSchedule.current = undefined;
		isPolling.current = false;
		isPaused.current = false;
		startedAt.current = undefined;
		pausedAt.current = undefined;
		accumulatedPausedMs.current = 0;
		lastPoll.current = undefined;
	}

	function reset() {
		stop();
		setIsTimeout(false);
	}

	return {
		start,
		pause,
		resume,
		stop,
		reset,
		isTimeout,
	};
}
